import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiArrowRight, FiUsers } from "react-icons/fi";

import MainContainer from "../../../components/utils/MainContainer";
import Seo from "../../../components/utils/Seo";
import { BandsGridSkeleton } from "../../../components/utils/Skeleton";
import bandsService from "../../../api/services/bands";
import festivalsService from "../../../api/services/festivals";
import { slugify } from "../../../utils/utils";
import useReveal from "../../../hooks/useReveal";

import {
  Page,
  SectionLabel,
  PageH1,
  StatStrip,
  Stat,
  FiltersRow,
  TabChips,
  ChipsRail,
  FilterChip,
  Grid,
  BandCard,
  CardOverlay,
  BandName,
  MetaRow,
  MetaLeft,
  GenreChip,
  ProductsLink,
  EmptyState,
  LoadMoreRow,
  GhostButton,
} from "./BandsPageNew.styles";

const PAGE_SIZE = 12;
const MAX_FETCH_PAGES = 10;

/* Povuci sve stranice liste (backend vraća 12 po strani) */
const fetchAll = async (fetchPage) => {
  const first = await fetchPage(1);
  const firstItems = first?.data || [];
  const totalPages = Math.min(
    first?.pagination?.totalPages || 1,
    MAX_FETCH_PAGES,
  );

  let rest = [];
  if (totalPages > 1) {
    const responses = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 2)),
    );
    rest = responses.flatMap((response) => response?.data || []);
  }

  const seen = new Set();
  return [...firstItems, ...rest].filter((item) => {
    if (!item?.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

/* Broj koji se "izbroji" od 0 do vrednosti kad se stat traka pojavi */
const CountUp = ({ value, duration = 900 }) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplay(value);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(eased * value));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return <>{display}</>;
};

/* Kartica sa sopstvenim scroll-reveal observerom; --i je kolona u redu,
   pa red ulazi kaskadno sleva nadesno */
const RevealBandCard = ({ columnIndex, to, onClick, children }) => {
  const [ref, visible] = useReveal({ rootMargin: "0px 0px -40px 0px" });
  return (
    <BandCard
      as={Link}
      to={to}
      ref={ref}
      $visible={visible}
      style={{ "--i": columnIndex }}
      onClick={onClick}
    >
      {children}
    </BandCard>
  );
};

export default function BandsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // "festivali" novo, "festivals" legacy
  const tabParam = searchParams.get("tab");
  const isFestivals = tabParam === "festivali" || tabParam === "festivals";
  const zanrParam = searchParams.get("zanr") || "";
  const page = Math.max(1, parseInt(searchParams.get("page"), 10) || 1);

  const [bands, setBands] = useState(null); // null = nije učitano
  const [festivals, setFestivals] = useState(null);

  // Učitaj podatke aktivnog taba (jednom po tabu)
  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        if (isFestivals && festivals === null) {
          const items = await fetchAll((p) =>
            festivalsService.getFestivals({ page: p, limit: PAGE_SIZE }),
          );
          if (active) setFestivals(items);
        } else if (!isFestivals && bands === null) {
          const items = await fetchAll((p) =>
            bandsService.getBands({ page: p, limit: PAGE_SIZE }),
          );
          if (active) setBands(items);
        }
      } catch {
        if (active) {
          if (isFestivals) setFestivals([]);
          else setBands([]);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFestivals]);

  const items = isFestivals ? festivals : bands;
  const loading = items === null;

  const applyParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    if (!("page" in patch)) next.delete("page");
    if (next.get("page") === "1") next.delete("page");
    setSearchParams(next, { replace: true });
  };

  /* Žanrovi (samo bendovi) */
  const genres = useMemo(() => {
    if (isFestivals || !bands) return [];
    const unique = [...new Set(bands.map((b) => b.genre).filter(Boolean))];
    return unique.sort((a, b) => a.localeCompare(b, "sr"));
  }, [bands, isFestivals]);

  const selectedGenre = useMemo(
    () => genres.find((g) => slugify(g) === zanrParam) || null,
    [genres, zanrParam],
  );

  const filtered = useMemo(() => {
    if (!items) return [];
    if (isFestivals || !selectedGenre) return items;
    return items.filter((band) => band.genre === selectedGenre);
  }, [items, selectedGenre, isFestivals]);

  const visible = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = visible.length < filtered.length;

  /* Stat traka */
  const stats = useMemo(() => {
    const list = items || [];
    const products = list.reduce(
      (sum, item) => sum + (item.numOfProducts || 0),
      0,
    );
    const cities = new Set(list.map((i) => i.city).filter(Boolean)).size;
    if (isFestivals) {
      return [
        {
          value: list.length,
          label: list.length === 1 ? "festival" : "festivala",
        },
        { value: products, label: "proizvoda" },
        { value: cities, label: cities === 1 ? "grad" : "gradova" },
      ];
    }
    const genreCount = new Set(list.map((i) => i.genre).filter(Boolean)).size;
    return [
      { value: list.length, label: list.length === 1 ? "bend" : "bendova" },
      { value: products, label: "proizvoda" },
      { value: genreCount, label: genreCount === 1 ? "žanr" : "žanra" },
      { value: cities, label: cities === 1 ? "grad" : "gradova" },
    ];
  }, [items, isFestivals]);

  return (
    <MainContainer>
      <Seo title="Bendovi" canonical="/bendovi" />
      <Page>
        <SectionLabel>Scena</SectionLabel>
        <PageH1>{isFestivals ? "Festivali" : "Bendovi"}</PageH1>

        {!loading && (
          <StatStrip>
            {stats.map((stat) => (
              <Stat key={stat.label}>
                <strong>
                  <CountUp value={stat.value} />
                </strong>
                <span>{stat.label}</span>
              </Stat>
            ))}
          </StatStrip>
        )}

        <FiltersRow>
          <TabChips>
            <FilterChip
              type="button"
              $active={!isFestivals}
              onClick={() => applyParams({ tab: null, zanr: null })}
            >
              Bendovi
            </FilterChip>
            <FilterChip
              type="button"
              $active={isFestivals}
              onClick={() => applyParams({ tab: "festivali", zanr: null })}
            >
              Festivali
            </FilterChip>
          </TabChips>

          {!isFestivals && genres.length > 0 && (
            <ChipsRail>
              <FilterChip
                type="button"
                $active={!selectedGenre}
                onClick={() => applyParams({ zanr: null })}
              >
                Svi žanrovi
              </FilterChip>
              {genres.map((genre) => (
                <FilterChip
                  key={genre}
                  type="button"
                  $active={selectedGenre === genre}
                  onClick={() => applyParams({ zanr: slugify(genre) })}
                >
                  {genre}
                </FilterChip>
              ))}
            </ChipsRail>
          )}
        </FiltersRow>

        {loading ? (
          <BandsGridSkeleton count={6} />
        ) : filtered.length === 0 ? (
          <EmptyState>
            <FiUsers size={40} />
            <p>
              {isFestivals
                ? "Trenutno nema festivala."
                : "Nema bendova za ovaj filter."}
            </p>
          </EmptyState>
        ) : (
          <>
            <Grid>
              {visible.map((item, index) => (
                <RevealBandCard
                  key={item.id}
                  columnIndex={index % 3}
                  to={
                    isFestivals
                      ? `/festival/${item.slug || item.id}`
                      : `/bend/${item.slug || item.id}`
                  }
                  onClick={() => {
                    setTimeout(() => {
                      window.scrollTo(0, 0);
                    }, 300);
                  }}
                >
                  {(item.bannerImage || item.mainImage) && (
                    <img
                      src={item.bannerImage || item.mainImage}
                      alt={item.name}
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <CardOverlay>
                    <BandName>{item.name}</BandName>
                    <MetaRow>
                      <MetaLeft>
                        {item.genre && <GenreChip>{item.genre}</GenreChip>}
                        {item.city && <span>{item.city}</span>}
                        {item.city && item.founded && (
                          <span className="dot">·</span>
                        )}
                        {item.founded && <span>{item.founded}</span>}
                      </MetaLeft>
                      <ProductsLink>
                        {item.numOfProducts || 0}{" "}
                        {(item.numOfProducts || 0) === 1
                          ? "proizvod"
                          : "proizvoda"}
                        <FiArrowRight size={14} />
                      </ProductsLink>
                    </MetaRow>
                  </CardOverlay>
                </RevealBandCard>
              ))}
            </Grid>

            {hasMore && (
              <LoadMoreRow>
                <GhostButton
                  type="button"
                  onClick={() => applyParams({ page: page + 1 })}
                >
                  Učitaj još ({filtered.length - visible.length})
                </GhostButton>
              </LoadMoreRow>
            )}
          </>
        )}
      </Page>
    </MainContainer>
  );
}
